<script>
  /**Dependencies*/
  import { afterUpdate, onMount } from "svelte";
  import { goto } from "$app/navigation";
  import { dispatchCustomEvent, SEARCH } from "$lib/utils/events";

  /**Component*/
  import ScrollingXSlider from "$lib/components/global/ScrollingXSlider.svelte";
  import TinyCardProducts from "$lib/components/global/TinyCardProducts.svelte";
  import SearchRecomendations from "./SearchRecomendations.svelte";
  import { findInSiteMap } from "$lib/utils/searchConsultPages";
  import CardMenu from "./CardMenu.svelte";

  /**@typedef {import ('$lib/types/index').PublicProduct} product*/

  /**@type {string[]}*/
  let searchResults = [];
  /**@type {product[][] | ??}*/
  let searchProducts;
  /** @type {HTMLInputElement}*/
  let searchInput;
  /**@type {string}*/
  let searchContent = "";
  /**@type {number}*/
  let viewportWidth;
  /**@type {any}*/
  let pagesSearch;

  /**@type {HTMLDialogElement}*/
  let modal;
  /**@type {boolean}*/
  let isOpen = false;
  let isFirstOpen = false;

  const openModal = () => {
    modal.showModal();
    searchInput.select();
    isOpen = true;
  };
  const closeModal = () => {
    modal.close();
    isFirstOpen = true;

    /**@type {HTMLDialogElement | ??}*/
    let menuMobile = document.querySelector("#menu-mobile");
    if (menuMobile) {
      menuMobile.close();
    }
  };

  afterUpdate(() => {
    modal.addEventListener("close", () => {
      isOpen = false;
      searchContent = "";
      searchResults = [];
    });
    return () => {
      modal.removeEventListener("close", () => {
        isOpen = false;
        searchContent = "";
        searchResults = [];
      });
    };
  });

  /**@type {number}*/
  let timerId;
  let signalController = new AbortController();
  let fetchInProgress = false;
  /**@type {string}*/
  let historyOfSearch;
  async function fetchResults() {
    isFirstOpen = false;

    fetchInProgress = true;
    clearTimeout(timerId);
    if (searchInput?.value?.length < 1) {
      if (fetchInProgress) {
        signalController?.abort();
      }
      searchResults = [];
      return;
    }
    try {
      timerId = setTimeout(async () => {
        if (fetchInProgress) {
          signalController?.abort();
          signalController = new AbortController();
        }
        if (viewportWidth < 500) {
          closeModal();
          goto(`/search/results?s=${searchContent}`);
        } else {
          let search = await fetch(`/search?q=${searchContent}`, {
            signal: signalController?.signal,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          });
          searchResults = await search.json();
          pagesSearch = findInSiteMap(searchContent);
          if (search.status === 200) {
            dispatchCustomEvent(SEARCH, searchContent);
            fetchInProgress = false;
            historyOfSearch = searchContent;
          }
          if (searchResults.length > 0) {
            searchProducts = searchResults?.map((item) => [item]);
          } else {
            searchProducts = null;
          }
        }
      }, 500);
    } catch (error) {
      console.error(error);
    }
  }

  /**This function checks the cursor click or where it is tapped and closes the modal when you click or tap outside the modal area*/
  /**@param {*}e*/
  const closeByBackdrop = (e) => {
    let rect = e.target.getBoundingClientRect();
    if (
      rect.left > e.clientX ||
      rect.right < e.clientX ||
      rect.top > e.clientY ||
      rect.bottom < e.clientY
    ) {
      modal.close();
    }
  };

  function sendingSearch() {
    goto(`/search/results?s=${searchContent}`);
    closeModal();
  }
  /**@type {number}*/
  let viewportHeight;
  // @ts-ignore
  const bodyScroll = {
    prevent() {
      // @ts-ignore
      document.querySelector("body").style.overflow = "hidden";
    },
    allow() {
      // @ts-ignore
      document.querySelector("body").style.overflow = null;
    },
  };

  onMount(() => {
    isFirstOpen = true;
  });

  $: {
    isOpen;
    afterUpdate(() => {
      if (isOpen) {
        bodyScroll.prevent();
      } else {
        bodyScroll.allow();
      }
    });
  }
</script>

<svelte:window bind:innerHeight={viewportHeight} bind:innerWidth={viewportWidth} />

<div class="search">
  <div class="mobile">
    {#if !isOpen}
      <button on:click={openModal} aria-labelledby="search">
        <img loading="eager" src="/images/svg/mobileSearchSvg.svg" alt="search mobile icon" />
      </button>
    {/if}
  </div>
</div>
<!-- svelte-ignore a11y-click-events-have-key-events -->
<dialog on:click={closeByBackdrop} bind:this={modal}>
  <form on:submit|preventDefault={sendingSearch} method="get">
    <input
      type="text"
      name="q"
      bind:this={searchInput}
      on:keyup|preventDefault={fetchResults}
      bind:value={searchContent}
      placeholder="Search"
      autocomplete="off"
    />
    <button type="button" on:click={closeModal} class="close-modal" aria-labelledby="close-modal">
      <img loading="eager" src="/images/svg/closeSvg.svg" alt="close modal" />
    </button>
  </form>

  {#if isFirstOpen}
    <SearchRecomendations />
  {:else if searchResults?.length >= 0}
    <div class="results-container">
      {#if searchContent?.length > 0}
        <div class="text-results">
          {#if fetchInProgress}
            <div class="loading">
              <span>L</span>
              <span>o</span>
              <span>a</span>
              <span>d</span>
              <span>i</span>
              <span>n</span>
              <span>g</span>
            </div>
          {:else}
            <p>{searchResults?.length} results for "{searchContent}"</p>
            <a
              href={`/search/results?s=${searchContent}`}
              on:click={closeModal}
              aria-labelledby="see-all"
              class="btn-black"
            >
              <span style="color: #fdfdfd;">SEE ALL</span>
              <span class="label" id="see-all">SEE ALL</span>
            </a>
          {/if}
        </div>
      {:else if searchProducts?.length > 0 && searchContent?.length <= 0}
        <div class="text-results">
          <p>{searchProducts?.length} results for "{historyOfSearch}"</p>
          <a
            href={`/search/results?s=${historyOfSearch}`}
            on:click={closeModal}
            aria-labelledby="see-all"
          >
            SEE ALL
            <span class="label" id="see-all">SEE ALL</span>
          </a>
        </div>
      {/if}
      <div class="results">
        {#if pagesSearch && pagesSearch.length > 0}
          <div class="recomended-pages">
            {#each pagesSearch as page}
              <li on:click={() => closeModal()}>
                <CardMenu images={page.images} title={page.title} link={page.link} />
              </li>
            {/each}
          </div>
        {/if}
        <div class="suggestion-results">
          <ScrollingXSlider>
            {#if searchProducts?.length > 0}
              <div class="products-container">
                {#each searchProducts as result, index}
                  {#if result[0]?.images.image !== undefined}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <div class="product" on:click={closeModal}>
                      <!-- <CardProducts productList={result} /> -->
                      <TinyCardProducts product={result[0]} />
                    </div>
                  {/if}
                {/each}
              </div>
            {/if}
          </ScrollingXSlider>
        </div>
      </div>
    </div>
  {:else if searchContent !== ""}
    <div class="results-container">
      <div class="text-results">
        <p>{searchResults?.length} results for "{searchContent}"</p>
      </div>
    </div>
  {/if}
</dialog>

<style>
  .mobile button {
    width: 22px;
    aspect-ratio: 1/1;
  }
  button {
    background: transparent;
    border: none;
    grid-column: 1;
    grid-row: 1;
    z-index: 1;
    width: fit-content;
    justify-self: center;
    height: fit-content;
    align-self: center;
    cursor: pointer;
    z-index: 4;
  }
  dialog {
    min-width: 100svw;
    height: fit-content;
    background-color: #fff;
    top: 0;
    left: 0;
    border: none;
    z-index: 50;
    overflow: hidden;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  dialog[open] {
    display: flex;
    min-height: 30svh;
    padding: 6rem 0;
  }
  dialog::backdrop {
    background-color: rgba(0, 0, 0, 0.61);
    top: 60px;
  }
  dialog > form {
    width: 100%;
    max-width: 1420px;
    height: 80px;
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin: 0 0 60px 0;
  }
  dialog > form > input {
    border-radius: 30px;
    border: 1px solid #707070;
    width: 80%;
    height: 55px;
    padding: 2px 30px;
    font-size: 0.875em;
    font-weight: 600;
    outline: none;
  }
  .results-container {
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .text-results {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1366px;
    margin-top: -30px;
  }
  .text-results p {
    font-size: 1.3rem!important;
    font-family: "gotham-bold";
  }
  /* results */
  .results {
    display: grid;
    width: 95%;
    margin: 0 auto;
    overflow: hidden;
  }
  .recomended-pages {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    width: 100%;
    padding: 10px 0;
    max-width: 1366px;
    margin: 25px auto 0 auto;
    overflow-x: auto;
    list-style: none;
  }
  .results-container p {
    text-align: center;
    font-size: 0.9rem;
    font-weight: 500;
  }
  .results-container a {
    text-align: center;
    font-size: 0.8rem;
    text-transform: uppercase;
    text-decoration-color: #707070;
    color: #000;
    display: block;
  }
  .suggestion-results {
    width: 95svw;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
  .products-container {
    display: flex;
    gap: 1.5rem;
    width: 100%;
    margin-top: 25px;
    padding: 0 10px 0 0;
  }
  .product {
    min-width: 220px;
  }
  .loading {
    font-size: 0.8em;
    font-weight: 800;
    text-align: center;
    letter-spacing: 3px;
    width: 100%;
    height: 100%;
    font-family: "gotham-bold";
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1px;
  }
  .loading span {
    color: #1e2225;
    display: inline-block;
    margin: 0 -0.05em;
  }
  .loading span {
    animation: loading 0.7s infinite;
  }
  .loading span:nth-child(2) {
    animation-delay: 0.1s;
  }
  .loading span:nth-child(3) {
    animation-delay: 0.2s;
  }
  .loading span:nth-child(4) {
    animation-delay: 0.3s;
  }
  .loading span:nth-child(5) {
    animation-delay: 0.4s;
  }
  .loading span:nth-child(6) {
    animation-delay: 0.5s;
  }
  .loading span:nth-child(7) {
    animation-delay: 0.6s;
  }
  @keyframes loading {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(4px);
    }
  }
  @media (min-width: 768px) {
    dialog[open] {
      padding: 3rem 0 2rem 0;
    }
    .product {
      aspect-ratio: inherit;
    }
  }
  @media (min-width: 820px) {
    .product {
      min-width: 280px;
    }
  }
  @media (max-width: 500px) {
    .mobile button {
      width: 19px;
      aspect-ratio: 1/1;
    }
    dialog {
      height: 30svh;
      overflow: hidden;
      padding-top: 10px;
      width: 95svw;
      margin: 0 auto;
    }
    dialog form {
      height: 40px;
    }
    dialog form input {
      font-size: 1rem;
      padding: 0 5px;
      border: none;
      border-bottom: 1px solid #707070;
      border-radius: 0;
    }
    .products-container {
      display: none;
    }
  }
</style>
